/**
 * Content Chunking
 *
 * Splits large content into 256KB chunks for efficient P2P sync.
 * Each chunk is stored as a separate DAG object with a manifest
 * that lists all chunks in order.
 */

import { createDAGObject, computeHash } from '../dag/object.js';
import type { DAGStore } from '../dag/store.js';
import type { KeyPair } from '../identity/keys.js';
import { signObject } from '../identity/signing.js';
import {
  DEFAULT_CHUNK_SIZE,
  type ChunkContent,
  type ChunkManifestContent,
  type Chunk,
  type ChunkManifest,
} from './types.js';

// ============================================================================
// Chunking
// ============================================================================

/**
 * Result of chunking operation
 */
export interface ChunkResult {
  /** Manifest hash (use this to reference the content) */
  manifestHash: string;
  /** SHA-256 of original content */
  sha256: string;
  /** Total size in bytes */
  size: number;
  /** Number of chunks */
  chunkCount: number;
  /** Individual chunk hashes */
  chunkHashes: string[];
}

/**
 * Compute SHA-256 hash of data
 */
async function sha256(data: Uint8Array): Promise<string> {
  // Create a clean ArrayBuffer copy to avoid SharedArrayBuffer issues
  const buffer = new ArrayBuffer(data.length);
  new Uint8Array(buffer).set(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = new Uint8Array(hashBuffer);
  return Array.from(hashArray)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convert Uint8Array to base64 string
 */
function toBase64(data: Uint8Array): string {
  // Browser-compatible base64 encoding
  const binary = Array.from(data)
    .map(b => String.fromCharCode(b))
    .join('');
  return btoa(binary);
}

/**
 * Convert base64 string to Uint8Array
 */
function fromBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Chunk content and store in DAG
 *
 * Creates:
 * 1. Individual chunk objects (clipboard.chunk)
 * 2. A manifest object (clipboard.manifest) that references all chunks
 *
 * Returns the manifest hash which should be used to reference the content.
 */
export async function chunkContent(
  data: Uint8Array,
  mimeType: string,
  keyPair: KeyPair,
  dagStore: DAGStore,
  options?: {
    chunkSize?: number;
  }
): Promise<ChunkResult> {
  const chunkSize = options?.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const totalSize = data.length;
  const contentSha256 = await sha256(data);

  // Calculate number of chunks
  const chunkCount = Math.ceil(totalSize / chunkSize);

  // We'll create the manifest first (without hash), then create chunks
  // with reference to manifest, then finalize manifest with chunk hashes
  const chunkHashes: string[] = [];

  // Placeholder manifest hash (will be computed after all chunks)
  const manifestPlaceholder = 'pending';

  // Create chunks
  for (let i = 0; i < chunkCount; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, totalSize);
    const chunkData = data.slice(start, end);

    const chunkContent: ChunkContent = {
      index: i,
      total: chunkCount,
      manifest: manifestPlaceholder, // Will be updated in manifest
      data: toBase64(chunkData),
    };

    // Create and sign chunk
    const chunkObj = createDAGObject<ChunkContent>({
      type: 'clipboard.chunk',
      creator: keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents: [],
      content: chunkContent,
    });
    const signedChunk = signObject(chunkObj, keyPair);

    // Store chunk
    await dagStore.put(signedChunk);
    chunkHashes.push(signedChunk.hash);
  }

  // Create manifest
  const manifestContent: ChunkManifestContent = {
    size: totalSize,
    sha256: contentSha256,
    chunks: chunkHashes,
    mimeType,
  };

  const manifestObj = createDAGObject<ChunkManifestContent>({
    type: 'clipboard.manifest',
    creator: keyPair.publicKeyBase58,
    timestamp: Date.now(),
    parents: chunkHashes, // Reference all chunks
    content: manifestContent,
  });
  const signedManifest = signObject(manifestObj, keyPair);

  // Store manifest
  await dagStore.put(signedManifest);

  return {
    manifestHash: signedManifest.hash,
    sha256: contentSha256,
    size: totalSize,
    chunkCount,
    chunkHashes,
  };
}

/**
 * Reassemble content from chunks
 *
 * Fetches the manifest and all chunks, verifies integrity,
 * and returns the original content.
 */
export async function reassembleContent(
  manifestHash: string,
  dagStore: DAGStore
): Promise<Uint8Array> {
  // Fetch manifest
  const manifest = await dagStore.get(manifestHash) as ChunkManifest | undefined;
  if (!manifest) {
    throw new Error(`Manifest not found: ${manifestHash}`);
  }

  if (manifest.type !== 'clipboard.manifest') {
    throw new Error(`Invalid manifest type: ${manifest.type}`);
  }

  const { chunks, size, sha256: expectedHash } = manifest.content;

  // Fetch and reassemble chunks in order
  const result = new Uint8Array(size);
  let offset = 0;

  for (let i = 0; i < chunks.length; i++) {
    const chunkHash = chunks[i];
    const chunk = await dagStore.get(chunkHash) as Chunk | undefined;

    if (!chunk) {
      throw new Error(`Chunk ${i} not found: ${chunkHash}`);
    }

    if (chunk.type !== 'clipboard.chunk') {
      throw new Error(`Invalid chunk type: ${chunk.type}`);
    }

    if (chunk.content.index !== i) {
      throw new Error(`Chunk index mismatch: expected ${i}, got ${chunk.content.index}`);
    }

    const chunkData = fromBase64(chunk.content.data);
    result.set(chunkData, offset);
    offset += chunkData.length;
  }

  // Verify content hash
  const actualHash = await sha256(result);
  if (actualHash !== expectedHash) {
    throw new Error(`Content hash mismatch: expected ${expectedHash}, got ${actualHash}`);
  }

  return result;
}

/**
 * Get chunk status for a manifest
 *
 * Returns information about which chunks are available locally.
 */
export async function getChunkStatus(
  manifestHash: string,
  dagStore: DAGStore
): Promise<{
  manifest: ChunkManifest | null;
  available: boolean[];
  missing: string[];
  complete: boolean;
  progress: number;
}> {
  const manifest = await dagStore.get(manifestHash) as ChunkManifest | undefined;

  if (!manifest || manifest.type !== 'clipboard.manifest') {
    return {
      manifest: null,
      available: [],
      missing: [manifestHash],
      complete: false,
      progress: 0,
    };
  }

  const { chunks } = manifest.content;
  const available: boolean[] = [];
  const missing: string[] = [];

  for (const chunkHash of chunks) {
    const exists = await dagStore.has(chunkHash);
    available.push(exists);
    if (!exists) {
      missing.push(chunkHash);
    }
  }

  const complete = missing.length === 0;
  const progress = chunks.length > 0 ? (chunks.length - missing.length) / chunks.length : 1;

  return {
    manifest,
    available,
    missing,
    complete,
    progress,
  };
}

/**
 * Check if data needs chunking (larger than chunk size)
 */
export function needsChunking(data: Uint8Array, chunkSize?: number): boolean {
  return data.length > (chunkSize ?? DEFAULT_CHUNK_SIZE);
}

/**
 * Store small content directly without chunking
 *
 * For content smaller than chunk size, we still create a manifest
 * for consistency, but with just one chunk.
 */
export async function storeSmallContent(
  data: Uint8Array,
  mimeType: string,
  keyPair: KeyPair,
  dagStore: DAGStore
): Promise<ChunkResult> {
  // For small content, just use the chunking function with 1 chunk
  return chunkContent(data, mimeType, keyPair, dagStore, {
    chunkSize: data.length + 1, // Ensure single chunk
  });
}
